import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Flame, CheckCircle2, ArrowRight, Clock, KeyRound, Eye, EyeOff, ArrowLeft, ShieldCheck, RefreshCw } from "lucide-react";

export default function Login() {
  const { t } = useI18n();
  const [tab, setTab] = useState("login");

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid lg:grid-cols-2 gap-10 items-center">
      <div className="hidden lg:block">
        <div className="rounded-3xl overflow-hidden card-warm relative">
          <img src="https://images.unsplash.com/photo-1633368516165-f7b04d6428d1?w=900" alt="brass diya" className="w-full h-[560px] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-tr from-[#5C061C]/70 via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
            <p className="font-serif text-3xl leading-tight">{t("invocation")}</p>
            <p className="mt-2 opacity-90 text-sm">{t("tagline")}</p>
          </div>
        </div>
      </div>

      <div className="card-warm rounded-3xl p-6 sm:p-8">
        <div className="flex justify-center mb-6">
          <div className="h-16 w-16 rounded-full bg-[#5C061C] grid place-items-center text-[#EAB308] font-serif text-3xl">ॐ</div>
        </div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-2 w-full bg-[#F4EEDF] mb-6">
            <TabsTrigger value="login" data-testid="auth-tab-login">{t("login.login")}</TabsTrigger>
            <TabsTrigger value="register" data-testid="auth-tab-register">{t("login.register")}</TabsTrigger>
          </TabsList>
          <TabsContent value="login"><LoginForm onSwitch={() => setTab("register")} /></TabsContent>
          <TabsContent value="register"><RegisterForm onSwitch={() => setTab("login")} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function PasswordInput({ value, onChange, placeholder, testid }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input type={show ? "text" : "password"} value={value} onChange={onChange} placeholder={placeholder} className="h-12 pr-10 bg-white border-[#E8DEC8]" data-testid={testid} />
      <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6E6259]" tabIndex={-1}>
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

function LoginForm({ onSwitch }) {
  const { t } = useI18n();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  const submit = async () => {
    if (!/^\d{10}$/.test(mobile)) return toast.error("Enter a valid 10-digit mobile");
    if (!password) return toast.error("Enter your password");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { mobile, password });
      login(data.token, data.user);
      toast.success("Welcome!");
      navigate("/");
    } catch (e) { toast.error(e.response?.data?.detail || "Login failed"); }
    finally { setBusy(false); }
  };

  if (forgotOpen) return <ForgotPassword onBack={() => setForgotOpen(false)} />;

  return (
    <div className="space-y-4">
      <h2 className="font-serif text-2xl text-[#5C061C] text-center">{t("login.login")}</h2>

      <div>
        <Label className="text-xs uppercase tracking-widest text-[#B45309]">Mobile</Label>
        <div className="flex gap-2 mt-1">
          <div className="h-12 px-3 rounded-md border border-[#E8DEC8] bg-white grid place-items-center font-semibold text-[#5C061C]">+91</div>
          <Input value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder={t("login.mobile_placeholder")} className="h-12 text-lg bg-white border-[#E8DEC8]" data-testid="login-mobile-input" inputMode="numeric" />
        </div>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-widest text-[#B45309]">{t("login.password")}</Label>
        <div className="mt-1"><PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("login.password")} testid="login-password-input" /></div>
      </div>

      <Button onClick={submit} disabled={busy} className="btn-saffron w-full h-12 rounded-full text-base" data-testid="login-submit-button">
        <Flame className="h-4 w-4 mr-2" /> {busy ? "Signing in..." : t("login.login")}
      </Button>

      <div className="flex items-center justify-between text-sm">
        <button onClick={() => setForgotOpen(true)} className="text-[#5C061C] hover:underline" data-testid="forgot-password-link">
          {t("login.forgot")}?
        </button>
        <button onClick={onSwitch} className="text-[#D97706] font-semibold" data-testid="switch-to-register">
          {t("login.new_here")} {t("login.register")} <ArrowRight className="h-3 w-3 inline" />
        </button>
      </div>
    </div>
  );
}

function ForgotPassword({ onBack }) {
  const { t } = useI18n();
  const [step, setStep] = useState(1);
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [mockOtp, setMockOtp] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);

  const sendOtp = async () => {
    if (!/^\d{10}$/.test(mobile)) return toast.error("Enter a valid 10-digit mobile");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/send-otp", { mobile });
      if (data.mock && data.otp) { setMockOtp(data.otp); toast.success(`Mock OTP: ${data.otp}`); }
      else toast.success("OTP sent");
      setStep(2);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    if (otp.length !== 6) return toast.error("Enter the 6-digit OTP");
    if (newPw.length < 6) return toast.error("Password must be at least 6 characters");
    if (newPw !== confirmPw) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { mobile, otp, new_password: newPw });
      toast.success("Password updated. Please login.");
      onBack();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="text-sm text-[#5C061C] inline-flex items-center gap-1" data-testid="forgot-back-button">
        <ArrowLeft className="h-3 w-3" /> {t("common.back")}
      </button>
      <h2 className="font-serif text-2xl text-[#5C061C] flex items-center gap-2"><KeyRound className="h-5 w-5" /> {t("login.forgot_title")}</h2>
      <p className="text-sm text-[#6E6259]">{t("login.forgot_subtitle")}</p>

      {step === 1 ? (
        <>
          <div className="flex gap-2">
            <div className="h-12 px-3 rounded-md border border-[#E8DEC8] bg-white grid place-items-center font-semibold text-[#5C061C]">+91</div>
            <Input value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder={t("login.mobile_placeholder")} className="h-12 text-lg bg-white border-[#E8DEC8]" data-testid="forgot-mobile-input" inputMode="numeric" />
          </div>
          <Button onClick={sendOtp} disabled={busy} className="btn-saffron w-full h-12 rounded-full" data-testid="forgot-send-otp-button">
            {busy ? "Sending..." : t("login.send")}
          </Button>
        </>
      ) : (
        <>
          <div className="rounded-lg bg-[#F4EEDF] border border-[#E8DEC8] p-3 text-xs text-[#6E6259]">
            OTP sent to +91 {mobile}. {mockOtp && <>Mock OTP: <span className="font-bold text-[#5C061C]">{mockOtp}</span></>}
          </div>
          <div className="flex justify-center">
            <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="forgot-otp-input">
              <InputOTPGroup>
                {[0,1,2,3,4,5].map((i) => <InputOTPSlot key={i} index={i} className="h-11 w-11 border-[#E8DEC8]" />)}
              </InputOTPGroup>
            </InputOTP>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#B45309]">{t("login.new_password")}</Label>
            <div className="mt-1"><PasswordInput value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder={t("login.new_password")} testid="forgot-new-password-input" /></div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#B45309]">{t("login.confirm_password")}</Label>
            <div className="mt-1"><PasswordInput value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} placeholder={t("login.confirm_password")} testid="forgot-confirm-password-input" /></div>
          </div>
          <Button onClick={submit} disabled={busy} className="btn-saffron w-full h-12 rounded-full" data-testid="forgot-submit-button">
            {busy ? "Updating..." : t("login.reset_password")}
          </Button>
        </>
      )}
    </div>
  );
}

function RegisterForm({ onSwitch }) {
  const { t } = useI18n();
  const [step, setStep] = useState(1);
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [mockOtp, setMockOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [form, setForm] = useState({ name: "", dob: "", time_of_birth: "", place_of_birth: "", address: "", year_joined: "", joined_via_event: "", whatsapp: "", email: "", language: "en" });
  const [captcha, setCaptcha] = useState(null);
  const [captchaAnswer, setCaptchaAnswer] = useState("");

  const loadCaptcha = async () => {
    try {
      const { data } = await api.post("/auth/captcha");
      setCaptcha(data);
      setCaptchaAnswer("");
    } catch { toast.error("Could not load CAPTCHA"); }
  };

  const sendOtp = async () => {
    if (!/^\d{10}$/.test(mobile)) return toast.error("Enter a valid 10-digit mobile");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/send-otp", { mobile });
      if (data.mock && data.otp) { setMockOtp(data.otp); toast.success(`Mock OTP: ${data.otp}`); }
      else toast.success("OTP sent");
      await loadCaptcha();
      setStep(2);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    if (otp.length !== 6) return toast.error("Enter 6 digits of the OTP");
    if (!form.name.trim()) return toast.error("Full name is required");
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    if (password !== confirmPw) return toast.error("Passwords do not match");
    if (!captcha?.captcha_id) return toast.error("Please refresh the human check");
    if (!captchaAnswer.trim()) return toast.error("Please answer the human check");
    setBusy(true);
    try {
      const payload = { mobile, otp, password, ...form, year_joined: form.year_joined ? Number(form.year_joined) : undefined, captcha_id: captcha.captcha_id, captcha_answer: captchaAnswer };
      await api.post("/auth/register", payload);
      setStep(3);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Registration failed");
      // If CAPTCHA failed, refresh it for the next attempt
      if ((e.response?.data?.detail || "").toLowerCase().includes("captcha")) await loadCaptcha();
    }
    finally { setBusy(false); }
  };

  if (step === 3) return (
    <div className="text-center py-6 space-y-3" data-testid="register-success">
      <CheckCircle2 className="h-16 w-16 text-[#15803D] mx-auto" />
      <h2 className="font-serif text-2xl text-[#5C061C]">{t("login.pending_title")}</h2>
      <p className="text-[#6E6259] max-w-sm mx-auto">{t("login.pending_body")}</p>
      <div className="inline-flex items-center gap-2 text-xs text-[#B45309] bg-[#F4EEDF] border border-[#E8DEC8] rounded-full px-3 py-1.5 mt-2">
        <Clock className="h-3 w-3" /> Awaiting admin approval
      </div>
      <div>
        <Button onClick={onSwitch} variant="outline" className="rounded-full border-[#5C061C] text-[#5C061C] mt-2" data-testid="switch-to-login">{t("login.already_have")} → {t("login.login")}</Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <h2 className="font-serif text-2xl text-[#5C061C] text-center">{t("register.title")}</h2>
      <p className="text-center text-sm text-[#6E6259]">{t("register.subtitle")}</p>

      {step === 1 ? (
        <>
          <div className="flex gap-2">
            <div className="h-12 px-3 rounded-md border border-[#E8DEC8] bg-white grid place-items-center font-semibold text-[#5C061C]">+91</div>
            <Input value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder={t("login.mobile_placeholder")} className="h-12 text-lg bg-white border-[#E8DEC8]" data-testid="register-mobile-input" inputMode="numeric" />
          </div>
          <Button onClick={sendOtp} disabled={busy} className="btn-saffron w-full h-12 rounded-full text-base" data-testid="register-send-otp-button">
            <Flame className="h-4 w-4 mr-2" /> {busy ? "Sending..." : t("login.send")}
          </Button>
        </>
      ) : (
        <>
          <div className="rounded-lg bg-[#F4EEDF] border border-[#E8DEC8] p-3 text-xs text-[#6E6259]">
            OTP sent to +91 {mobile}. {mockOtp && <>Mock OTP: <span className="font-bold text-[#5C061C]">{mockOtp}</span></>}
          </div>

          <div className="flex justify-center">
            <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="register-otp-input">
              <InputOTPGroup>
                {[0,1,2,3,4,5].map((i) => <InputOTPSlot key={i} index={i} className="h-11 w-11 border-[#E8DEC8]" />)}
              </InputOTPGroup>
            </InputOTP>
          </div>

          <div className="pt-2">
            <p className="text-xs uppercase tracking-widest text-[#B45309] mb-2">{t("register.personal")}</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <F label={t("profile.name")}><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="reg-name" className="bg-white border-[#E8DEC8]" /></F>
              <F label={t("profile.email")}><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="reg-email" className="bg-white border-[#E8DEC8]" /></F>
              <F label={t("profile.dob")}><Input type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} data-testid="reg-dob" className="bg-white border-[#E8DEC8]" /></F>
              <F label={t("profile.time_of_birth")}><Input type="time" value={form.time_of_birth} onChange={(e) => setForm({ ...form, time_of_birth: e.target.value })} data-testid="reg-tob" className="bg-white border-[#E8DEC8]" /></F>
              <F label={t("profile.place_of_birth")}><Input value={form.place_of_birth} onChange={(e) => setForm({ ...form, place_of_birth: e.target.value })} data-testid="reg-pob" className="bg-white border-[#E8DEC8]" /></F>
              <F label={t("profile.whatsapp")}><Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} data-testid="reg-whatsapp" className="bg-white border-[#E8DEC8]" /></F>
            </div>
            <F label={t("profile.address")}><Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="reg-address" className="bg-white border-[#E8DEC8] mt-2" /></F>
          </div>

          <div className="pt-2">
            <p className="text-xs uppercase tracking-widest text-[#B45309] mb-2">{t("register.devotional")}</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <F label={t("profile.year_joined")}><Input type="number" value={form.year_joined} onChange={(e) => setForm({ ...form, year_joined: e.target.value })} data-testid="reg-year" className="bg-white border-[#E8DEC8]" /></F>
              <F label={t("profile.joined_event")}><Input value={form.joined_via_event} onChange={(e) => setForm({ ...form, joined_via_event: e.target.value })} data-testid="reg-event" className="bg-white border-[#E8DEC8]" /></F>
              <F label={t("profile.language")}>
                <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
                  <SelectTrigger data-testid="reg-language" className="bg-white border-[#E8DEC8]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="hi">हिन्दी</SelectItem>
                    <SelectItem value="mr">मराठी</SelectItem>
                    <SelectItem value="gu">ગુજરાતી</SelectItem>
                  </SelectContent>
                </Select>
              </F>
            </div>
          </div>

          <div className="pt-2">
            <p className="text-xs uppercase tracking-widest text-[#B45309] mb-2">{t("login.password")}</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <F label={t("login.set_password")}><PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 6 characters" testid="reg-password" /></F>
              <F label={t("login.confirm_password")}><PasswordInput value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} placeholder="Repeat password" testid="reg-confirm-password" /></F>
            </div>
          </div>

          <div className="pt-2">
            <p className="text-xs uppercase tracking-widest text-[#B45309] mb-2 flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> Human check</p>
            <div className="card-warm rounded-xl p-3 flex items-center gap-3" data-testid="captcha-block">
              <div className="flex-1">
                <p className="text-[#5C061C] font-semibold" data-testid="captcha-question">{captcha?.question || "Loading..."}</p>
                <Input value={captchaAnswer} onChange={(e) => setCaptchaAnswer(e.target.value)} placeholder="Your answer" className="mt-2 bg-white border-[#E8DEC8]" data-testid="captcha-answer-input" />
              </div>
              <button type="button" onClick={loadCaptcha} className="h-9 w-9 grid place-items-center rounded-full bg-[#F4EEDF] text-[#5C061C]" title="New question" data-testid="captcha-refresh">
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>
          </div>

          <Button onClick={submit} disabled={busy} className="btn-saffron w-full h-12 rounded-full text-base" data-testid="register-submit-button">
            {busy ? "Submitting..." : t("register.submit")}
          </Button>
          <Button variant="ghost" onClick={() => setStep(1)} className="w-full text-[#6E6259]">{t("common.back")}</Button>
        </>
      )}

      <p className="text-sm text-center text-[#6E6259]">
        {t("login.already_have")} <button onClick={onSwitch} className="text-[#D97706] font-semibold" data-testid="switch-to-login-inline">{t("login.login")} <ArrowRight className="h-3 w-3 inline" /></button>
      </p>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-[#6E6259]">{label}</Label>
      {children}
    </div>
  );
}
